<script setup lang="ts">
import { computed } from "vue";

interface Props {
  bank?: string;
  userName: string;
  number: string;
  form: {
    expDate?: {
      month: number;
      year: number;
    };
  };
  cvv?: string;
  isBackVisible?: boolean;
  isForm?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isBackVisible: false,
  cvv: "",
  isForm: false,
});

const isVisa = computed(() => props.number?.startsWith("4") ?? false);
const isAmex = computed(() => props.number?.startsWith("3") ?? false);
const isMastercard = computed(() => !isVisa.value && !isAmex.value && !!props.number);

const displayNumber = computed(() => {
  const raw = props.number?.replace(/\s/g, "") || "";
  if (!raw) return "xxxx xxxx xxxx xxxx";
  return raw.padEnd(16, "x").replace(/(.{4})/g, "$1 ").trim();
});

const displayExpiry = computed(() => {
  if (!props.form.expDate) return "00/00";
  const { month, year } = props.form.expDate;
  return `${String(month || 0).padStart(2, "0")}/${String(year || 0).padStart(2, "0")}`;
});

const emit = defineEmits<{
  (e: "toggleBackCard"): boolean;
}>();
</script>

<template>
  <!-- data-screenshot-exclude: dato de tarjeta fuera de la captura del widget de bugs (ver useScreenCapture.ts). Va en el componente, no en sus cuatro pantallas: una exclusion por call site
       es una que alguien va a olvidar. -->
  <div
    data-screenshot-exclude
    class="w-full rounded-xl relative text-white cursor-pointer"
    style="aspect-ratio: 1.586 / 1"
    :class="{
      'rotate-y-180 transition-transform duration-500': isBackVisible,
      'transition-transform duration-500': !isBackVisible,
    }"
    @click="emit('toggleBackCard')"
  >
    <!-- Front -->
    <div
      class="front w-full h-full absolute top-0 left-0 rounded-xl bg-blue-ef-400 p-5 flex flex-col justify-between"
      :class="{ hidden: isBackVisible }"
    >
      <div class="flex justify-between items-start">
        <p v-if="bank" class="text-xs font-semibold uppercase tracking-widest text-white/80">
          {{ bank }}
        </p>
        <div
          class="w-10 h-8 rounded-md bg-amber-300 border border-amber-500/30 flex items-center justify-center overflow-hidden ml-auto"
        >
          <svg class="w-6 h-5 opacity-60" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="6" width="20" height="8" rx="1" stroke="#92400e" stroke-width="1.5" />
            <line x1="6" y1="6" x2="6" y2="14" stroke="#92400e" stroke-width="1" />
            <line x1="18" y1="6" x2="18" y2="14" stroke="#92400e" stroke-width="1" />
            <line x1="2" y1="10" x2="22" y2="10" stroke="#92400e" stroke-width="1" />
          </svg>
        </div>
      </div>

      <div>
        <p class="font-mono text-xl tracking-[0.15em] mb-3">{{ displayNumber }}</p>
        <div class="flex justify-between items-end">
          <div class="min-w-0">
            <p class="text-[9px] text-white/50 uppercase tracking-wider">Titular</p>
            <p class="text-sm font-semibold truncate max-w-[200px]">
              {{ userName || "NOMBRE DEL TITULAR" }}
            </p>
          </div>
          <div class="flex items-end gap-3">
            <div class="text-right">
              <p class="text-[9px] text-white/50 uppercase tracking-wider">Válida hasta</p>
              <p class="text-sm font-semibold font-mono">{{ displayExpiry }}</p>
            </div>

            <svg v-if="isVisa" class="h-6 w-auto" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9.112 8.262L5.97 15.758H3.92L2.374 9.775c-.094-.368-.175-.503-.461-.658C1.447 8.864.677 8.627 0 8.479l.046-.217h3.3a.904.904 0 01.894.764l.817 4.338 2.018-5.102zm8.033 5.049c.008-1.979-2.736-2.088-2.717-2.972.006-.269.262-.555.822-.628a3.66 3.66 0 011.913.336l.34-1.59a5.207 5.207 0 00-1.814-.333c-1.917 0-3.266 1.02-3.278 2.479-.012 1.079.963 1.68 1.698 2.04.756.367 1.01.603 1.006.931-.005.504-.602.725-1.16.734-.975.015-1.54-.263-1.992-.473l-.351 1.642c.453.208 1.289.39 2.156.398 2.037 0 3.37-1.006 3.377-2.564m5.061 2.447H24l-1.565-7.496h-1.656a.883.883 0 00-.826.55l-2.909 6.946h2.036l.405-1.12h2.488zm-2.163-2.656l1.02-2.815.588 2.815zm-8.16-4.84l-1.603 7.496H8.34l1.605-7.496z"
              />
            </svg>

            <svg v-else-if="isMastercard" class="h-8 w-auto" viewBox="0 0 48 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="17" cy="15" r="12" fill="#EB001B" />
              <circle cx="31" cy="15" r="12" fill="#F79E1B" />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M24 6.5c2.8 2.2 4.5 5.6 4.5 9.5s-1.7 7.3-4.5 9.5c-2.8-2.2-4.5-5.6-4.5-9.5s1.7-7.3 4.5-9.5z"
                fill="#FF5F00"
              />
            </svg>

            <div v-else-if="isAmex" class="h-6 px-2 rounded-[3px] bg-white flex items-center justify-center">
              <span class="text-[#006FCF] font-black text-[11px] italic tracking-tight">AMEX</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Back -->
    <div
      class="back w-full h-full absolute top-0 left-0 rounded-xl bg-blue-ef-400"
      :class="{ hidden: !isBackVisible }"
    >
      <div class="w-full h-10 bg-black/40 mt-6" />
      <div class="px-5 mt-6">
        <div class="bg-white/90 h-10 rounded flex items-center justify-end px-3">
          <p class="font-mono text-sm text-gray-800 rotate-cvv" :class="{ 'rotate-cvv-visible': isBackVisible }">
            {{ cvv !== "" ? cvv : "XXX" }}
          </p>
        </div>
        <p class="text-[9px] text-white/50 mt-2">
          El código de seguridad aparece al reverso de la tarjeta.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.rotate-y-180 {
  transform: rotateY(180deg);
}

.rotate-cvv {
  transform: rotateY(180deg);
}

.front,
.back {
  backface-visibility: hidden;
  position: absolute;
  top: 0;
  left: 0;
}

.hidden {
  display: none;
}
</style>
